import React, { useEffect } from 'react';
import { OG_IMAGE_URL } from './config';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { LeadProvider } from './contexts/LeadContext';
import { usePageTracking } from './hooks/usePageTracking';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { TrustBar } from './components/TrustBar';
import { MeetTheQueens } from './components/MeetTheQueens';
import { QueenExperience } from './components/QueenExperience';
import { MostWanted } from './components/MostWanted';
import { TradeIn } from './components/TradeIn';
import { LeadForm } from './components/LeadForm/LeadForm';
import { HowItWorks } from './components/HowItWorks';
import { Faq } from './components/Faq';
import { Footer } from './components/Footer';
import { WhatsAppCTA } from './components/WhatsAppCTA';
import { SectionDivider } from './components/SectionDivider';

/**
 * Head tags.
 *
 * robots: REMOVE BEFORE LAUNCH — placeholder content is currently live, so the page
 * must not be indexed yet.
 *
 * og:image: rendered only once OG_IMAGE_URL holds a real approved asset. Emitting a tag
 * that points nowhere makes WhatsApp and Meta render a broken preview card.
 */
function useHeadMeta() {
  useEffect(() => {
    const tags: HTMLMetaElement[] = [];
    const add = (attr: 'name' | 'property', key: string, content: string) => {
      const meta = document.createElement('meta');
      meta.setAttribute(attr, key);
      meta.content = content;
      document.head.appendChild(meta);
      tags.push(meta);
    };

    // REMOVE BEFORE LAUNCH
    add('name', 'robots', 'noindex, nofollow');
    if (OG_IMAGE_URL) {
      add('property', 'og:image', OG_IMAGE_URL);
      add('name', 'twitter:image', OG_IMAGE_URL);
      add('name', 'twitter:card', 'summary_large_image');
    }
    return () => {
      tags.forEach(tag => document.head.removeChild(tag));
    };
  }, []);
}
function Landing() {
  const {
    t
  } = useLanguage();
  usePageTracking();
  useHeadMeta();
  return <div className="w-full overflow-x-hidden bg-white" data-id="element-0" data-mp="356tf9929ob6">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:bg-ink focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white" data-id="element-1" data-mp="3uct0lbuzo8ws">
        {t('nav.skip')}
      </a>
      <Header data-id="element-2" data-mp="864atjm8yebm" />
      <main id="main" data-id="element-3" data-mp="86cqfj9r7xqi">
        <Hero data-id="element-4" data-mp="3gle9ehqxh1en" />
        <TrustBar data-id="element-5" data-mp="3gkv0trn63pda" />
        <MeetTheQueens data-id="element-6" data-mp="3gmfwxlp4rgd9" />
        <SectionDivider data-id="element-7" data-mp="3glwg68b7uy0t" />
        <QueenExperience data-id="element-8" data-mp="3gj1rg4l66r1l" />
        <MostWanted data-id="element-9" data-mp="3bmtij8xkfu2v" />
        <TradeIn data-id="element-10" data-mp="3bncorsdusu12" />
        <LeadForm data-id="element-11" data-mp="3bnuvjiy56qn8" />
        <HowItWorks data-id="element-12" data-mp="3bk72ekwm0691" />
        <SectionDivider data-id="element-13" data-mp="3qaxrhl6tgr8g" />
        <Faq data-id="element-14" data-mp="3qbfur1rwc5pv" />
      </main>
      <Footer data-id="element-15" data-mp="3qcqy50hfjbgg" />
      <WhatsAppCTA data-id="element-16" data-mp="3qdaienujybxn" />
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-17" data-mp="3ljpiurdb99wc">
      <LeadProvider data-id="element-18" data-mp="3lieeapc1jy46">
        <Landing data-id="element-19" data-mp="2xuwuph2xk6ca" />
      </LeadProvider>
    </LanguageProvider>;
}